import moment from 'moment';
import pinterpolate from 'pinterpolate';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getFormattedDate } from 'utils/date';
import { extractFullName } from 'utils/string';
import { handleError } from 'utils/errorHandler';

import * as userService from 'services/user';
import * as designationService from 'services/designation';
import * as designationAreaService from 'services/designationArea';

import { EMPLOYEE_PROFILE } from 'constants/routes';
import { DATE_FORMAT_DEFAULT, REACT_DATEPICKER_DATE_FORMAT } from 'constants/appConstants';

import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import { EmploymentHistory } from '../../components';
import DatePicker from 'components/common/datepicker/DatePicker';
import ProfileHeader from '../components/profileHeader/ProfileHeader';
import { FormSelect } from 'components/common/form';
import type { SelectOption as FormSelectOption } from 'components/common/form/FormSelect';

interface DesignationItem {
  id: number;
  name: string;
  area?: { id: number; name: string };
}

type ProfileHeaderEmployee = React.ComponentProps<typeof ProfileHeader>['employee'];
type ProfileHeaderUser = React.ComponentProps<typeof ProfileHeader>['user'];

interface DesignationEmployee extends ProfileHeaderEmployee {
  id: number;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  designation?: DesignationItem;
  empStatusHistory?: EmpStatusHistoryItem[];
  designationAreaHistory?: DesignationHistoryItem[];
}

interface ChangeDesignationProps extends RouteComponentProps<{ id?: string }> {
  user: ProfileHeaderUser;
  match: { params: { id?: string } };
}

interface ChangeDesignationErrors {
  designation: boolean;
  transitionDate: boolean;
  area?: boolean;
}

interface DesignationHistoryItem {
  transitionDate: string | Date;
  area: { id: number; name: string };
  designation: { id: number; name: string };
}

interface EmpStatusHistoryItem {
  id?: number;
  transitionDate: string | Date;
  engagementStatus: { id: number; name: string };
}

interface FieldChangeEvent {
  target: {
    name: 'designation' | 'area' | 'transitionDate';
    value: string | number | Record<string, string | number | boolean | null> | null;
  };
}

export const ChangeDesignation: React.FC<ChangeDesignationProps> = ({ user, match }) => {
  const id = match.params.id;
  const [employee, setEmployee] = useState<DesignationEmployee | null>(null);
  const [designations, setDesignations] = useState<DesignationItem[] | null>(null);
  const [selectedArea, setSelectedArea] = useState<DesignationItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [loadingEmployee, setLoadingEmployee] = useState<boolean>(Boolean(id));
  const [designationAreas, setDesignationAreas] = useState<DesignationItem[] | null>(null);
  const [currentDesignation, setCurrentDesignation] = useState<DesignationItem | null>(null);
  const [loadingDesignations, setLoadingDesignations] = useState<boolean>(Boolean(id));
  const [selectedDesignation, setSelectedDesignation] = useState<DesignationItem | null>(null);
  const [transitionDate, setTransitionDate] = useState<string>(
    getFormattedDate(new Date().toISOString(), DATE_FORMAT_DEFAULT)
  );
  const [loadingDesignationAreas, setLoadingDesignationAreas] = useState<boolean>(Boolean(id));
  const [errors, setErrors] = useState<ChangeDesignationErrors>({
    designation: false,
    transitionDate: false,
  });

  const selectedFieldToState = useMemo(
    () => ({
      designation: 'selectedDesignation',
      area: 'selectedArea',
      transitionDate: 'transitionDate',
    }),
    []
  );

  const getPageTitle = useCallback((employeeData: DesignationEmployee | null) => {
    const employeeName = employeeData ? extractFullName(employeeData) : '';
    return employeeName ? `${employeeName}'s designation` : 'Change Designation';
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }

    const fetchPageData = async () => {
      try {
        const [employeeResponse, designationsResponse, designationAreasResponse] = (await Promise.all([
          userService.fetchById(id),
          designationService.fetchAll(),
          designationAreaService.fetchAll(),
        ])) as [
          DesignationEmployee,
          { data: Array<{ id: number; name: string }> },
          { data: Array<{ id: number; name: string }> },
        ];

        window.document.title = getPageTitle(employeeResponse);
        setEmployee({
          ...employeeResponse,
          designationAreaHistory: employeeResponse.designationAreaHistory?.slice().reverse() || [],
        });
        setCurrentDesignation(employeeResponse.designation || null);
        setSelectedArea(employeeResponse.designation?.area || null);
        setDesignations(
          designationsResponse.data.map((item) => ({
            id: Number(item.id),
            name: String(item.name),
          }))
        );
        setDesignationAreas(
          designationAreasResponse.data.map((item) => ({
            id: Number(item.id),
            name: String(item.name),
          }))
        );
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingEmployee(false);
        setLoadingDesignations(false);
        setLoadingDesignationAreas(false);
      }
    };

    void fetchPageData();
  }, [getPageTitle, id]);

  const getSelectedOptionValue = useCallback(
    (
      fieldName: FieldChangeEvent['target']['name'],
      fieldValue: string | number | Record<string, string | number | boolean | null> | null
    ) => {
      if (fieldName === 'designation') {
        return fieldValue && designations?.find((designation) => +fieldValue === designation.id);
      }
      if (fieldName === 'area') {
        return fieldValue && designationAreas?.find((area) => +fieldValue === area.id);
      }
      return fieldValue;
    },
    [designationAreas, designations]
  );

  const handleChange = useCallback(
    (event: FieldChangeEvent) => {
      const selectedValue = event?.target?.value;
      const selectedFieldName = event?.target?.name;

      const selectedValueFromOptions = getSelectedOptionValue(selectedFieldName, selectedValue);
      if (selectedFieldToState[selectedFieldName] === 'selectedDesignation') {
        setSelectedDesignation((selectedValueFromOptions as DesignationItem) || null);
      } else if (selectedFieldToState[selectedFieldName] === 'selectedArea') {
        setSelectedArea((selectedValueFromOptions as DesignationItem) || null);
      } else {
        setTransitionDate(String(selectedValueFromOptions || ''));
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        [selectedFieldName]: false,
      }));
    },
    [getSelectedOptionValue, selectedFieldToState]
  );

  const redirectToEmployeeProfile = useCallback(() => {
    if (!id) {
      return;
    }
    history.push(pinterpolate(EMPLOYEE_PROFILE, { id }));
  }, [id]);

  const handleSubmit = useCallback(async () => {
    const designation = selectedDesignation;
    const area = selectedArea;
    if (!designation || !transitionDate || !area || !id) {
      setErrors({
        designation: !designation,
        transitionDate: !transitionDate,
        area: !area,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await userService.updateDesignation({ designation, area, transitionDate }, id);
      toast.success({
        title: 'Success',
        message: 'Designation has been updated successfully.',
      });
      redirectToEmployeeProfile();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [id, redirectToEmployeeProfile, selectedArea, selectedDesignation, transitionDate]);

  const designationOptions: FormSelectOption[] = (designations || []).map((item) => ({
    label: item.name,
    value: item.id,
  }));
  const areaOptions: FormSelectOption[] = (designationAreas || []).map((item) => ({
    label: item.name,
    value: item.id,
  }));

  if (loadingEmployee) {
    return (
      <div className="loading-container container">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <div className="title profile-wrapper">
        <ProfileHeader employee={employee} user={user} />
      </div>
      <div className="container d-flex fd-row flex-fix name-wrap">
        <div className="full-scope-card momr-20">
          <div className="full-scope-card__header table-header name-wrap">
            <div className="d-flex flex-row">
              <h3>Change Designation</h3>
            </div>
          </div>
          <div className="leaveissuer-form">
            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Current Designation: </span>
              <span className="span-padding-left">{currentDesignation?.name || 'None'}</span>
            </label>

            <label className="font-label font-14 label-margin">
              <span className="font-weight-bold dark--text"> Current Designation Area: </span>
              <span className="span-padding-left">{currentDesignation?.area?.name || 'None'}</span>
            </label>

            {!loadingDesignations && (
              <FormSelect
                label="New Designation"
                name="designation"
                isMandatory={true}
                disabled={false}
                handleChange={handleChange}
                value={selectedDesignation ? selectedDesignation.id : null}
                error={errors.designation && 'Please enter Designation'}
                options={designationOptions}
              />
            )}
            {!loadingDesignationAreas && (
              <FormSelect
                label="Area"
                name="area"
                isMandatory={true}
                disabled={false}
                handleChange={handleChange}
                value={selectedArea ? selectedArea.id : null}
                error={errors.area && 'Please enter Area'}
                options={areaOptions}
              />
            )}
            <InputWrapper isMandatory={true} label="Transition Date" error={errors.transitionDate}>
              <DatePicker
                date={transitionDate ? moment(transitionDate) : null}
                placeholderText="Pick a Transition Date"
                dateFormat={REACT_DATEPICKER_DATE_FORMAT}
                displayFormat="LL"
                hasError={Boolean(errors.transitionDate)}
                openDirection="up"
                onDateChange={(selectedDate) =>
                  handleChange({
                    target: {
                      name: 'transitionDate',
                      value: moment(selectedDate).format('YYYY-MM-DD'),
                    },
                  })
                }
              />
            </InputWrapper>

            <button
              type="button"
              className="btn btn--outlined-grey f-left card-button mr-10"
              onClick={redirectToEmployeeProfile}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary f-right card-button mb-20"
              onClick={handleSubmit}
              disabled={loadingDesignationAreas || isSubmitting || loadingDesignations}
            >
              {isSubmitting ? <Loading /> : 'Change'}
            </button>
          </div>
        </div>
        <div className="full-scope-card emp-history-area emp-history-wrapper custom-scroll-bar">
          <div className="font-bold emp-history-title">Employment History</div>
          <EmploymentHistory
            designationAreaHistory={employee?.designationAreaHistory || []}
            empStatusHistory={employee?.empStatusHistory || []}
          />
        </div>
      </div>
    </>
  );
};
